<script setup lang="ts">
import { api } from '@/api';
import { type ConfirmEmailRequestDto, ConfirmEmailRequestDtoTypeEnum } from '@/api/generator';
import VeePasswordField from '@/components/forms/PasswordField/VeePasswordField.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import SimplePage from '@/components/pages/SimplePage/SimplePage.vue';
import PasswordQualityEstimation from '@/components/PasswordQualityEstimation/PasswordQualityEstimation.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { useZxcvbn } from '@/plugins/zxcvbn';
import { routenames } from '@/router/routenames';
import { useFieldValue } from 'vee-validate';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

type ActivationViewProps = {
  email: string;
  token: string;
};

const props = defineProps<ActivationViewProps>();

const initialValues: Partial<ConfirmEmailRequestDto> = {
  $type:
    ConfirmEmailRequestDtoTypeEnum.PtwPtw100AppZeroApplicationSharedAuthorizationAccountDtoConfirmEmailRequestDtoPtwPtw100AppZeroApplicationShared,
  email: props.email,
  token: props.token,
};

const { form } = useVeeForm<ConfirmEmailRequestDto>({
  schemaName: 'ConfirmEmailRequestDto',
  options: {
    initialValues,
  },
  mode: 'edit',
});
const { handleSubmit, meta } = form;
const password = useFieldValue<string>('newPassword');
const showError = ref<boolean | undefined>();

const router = useRouter();

const zxcvbnResult = useZxcvbn(password, [props.email]);

const submit = handleSubmit(async (confirmEmailRequestDto) => {
  const { success } = await api.account.confirmActivation(
    {
      confirmEmailRequestDto,
    },
    [
      formBackendErrorHandler(form),
      () => {
        // if formBackendErrorHandler (e.g. because of password-complexity) does not stop the error-propagation we will show the error-page
        showError.value = true;
      },
    ],
  );

  if (success) {
    // perform signOut (and navigate to signIn-page)
    // to make sure, the user is really redirected to
    // the signIn page (if another user should be logged in currently)
    await router.push({ name: routenames.signOut });
  }
});
</script>

<template>
  <SimplePage :headline="$t('ActivateAccount')">
    <p v-if="showError" class="text-pre-line">
      {{ $t('ContactAdminBecauseOfErrorMsg') }}
    </p>
    <template v-else>
      <p class="text-pre-line">
        {{ $t('ToActivateAccountSetPasswordMsg') }}
      </p>
      <form class="mt-6 form" @submit="submit">
        <input id="username" name="email" autocomplete="username" type="email" class="d-none" />
        <VeePasswordField
          :label="$t('Password')"
          name="newPassword"
          :hint="$t('PasswordComplexityViolationMsg')"
          :persistentHint="true"
          autocomplete="new-password"
        />
        <PasswordQualityEstimation :result="zxcvbnResult" />
        <VeeSubmitButton
          class="mt-6"
          :block="true"
          :text="$t('ActivateAccount')"
          :disabled="!meta.valid"
        />
      </form>
    </template>
  </SimplePage>
</template>

<style lang="scss" scoped>
@use '@/styles/variables';

.form {
  max-width: variables.$maxSimpleLayoutFormWidth;
  margin: 0 auto;
  text-align: left;
}
</style>
